"use client";

import { useId, useState, type FormEvent } from "react";
import { SproutRichText } from "../SproutRichText/SproutRichText";
import type { IFormPanelProps } from "./FormPanel.props";

const controlClass =
  "min-h-12 w-full rounded-[14px] border-2 border-input bg-mineral-gray px-4 font-sprout-body text-base text-foreground outline-none transition-[border-color,box-shadow] placeholder:text-basalt focus:border-navy-ink focus:ring-2 focus:ring-navy-ink/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring aria-[invalid=true]:border-oxidized-orange aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-oxidized-orange/15";

export const FormPanel = ({
  errorMessage,
  fields,
  submitLabel,
  successMessage,
}: IFormPanelProps) => {
  const baseId = useId();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const controls = Array.from(
      form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
        "input, select, textarea",
      ),
    );
    const nextErrors: Record<string, string> = {};
    for (const control of controls)
      if (!control.checkValidity()) nextErrors[control.name] = control.validationMessage;
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      controls.find((control) => !control.checkValidity())?.focus();
      return;
    }
    setSubmitting(true);
    setSubmissionFailed(false);
    try {
      if (!navigator.onLine) throw new Error("offline");
      await new Promise<void>((resolve) => window.setTimeout(resolve, 350));
      setSucceeded(true);
    } catch {
      setSubmissionFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (succeeded)
    return (
      <div
        aria-live="polite"
        className="sprout-workbook-paper sprout-paper-lift rounded-[1.5rem_1.5rem_1.5rem_0.35rem] border-2 border-navy-ink bg-leaf p-7 text-navy-ink md:p-10"
        role="status"
      >
        <SproutRichText
          className="max-w-[65ch] text-navy-ink [&_h3]:font-sprout-display [&_h3]:text-3xl [&_h3]:font-bold"
          content={successMessage}
        />
      </div>
    );

  return (
    <form
      className="sprout-workbook-paper sprout-chapter-tab sprout-paper-lift relative flex flex-col gap-6 rounded-[1.5rem_1.5rem_1.5rem_0.35rem] border-2 border-navy-ink p-7 pt-10 md:p-10 md:pt-12"
      noValidate
      onSubmit={submit}
    >
      {submissionFailed ? (
        <div
          className="-rotate-[0.4deg] rounded-[14px] border-2 border-oxidized-orange bg-primary-100 p-5"
          role="alert"
        >
          <SproutRichText className="text-base text-foreground" content={errorMessage} />
        </div>
      ) : null}
      {fields.map((field, index) => {
        const name = `field-${index}`;
        const inputId = `${baseId}-${index}`;
        const errorId = `${inputId}-error`;
        const error = errors[name];
        const shared = {
          "aria-describedby": error ? errorId : undefined,
          "aria-invalid": Boolean(error) as true | false,
          id: inputId,
          name,
          required: Boolean(field.required),
        };
        const label = (
          <span className="font-sprout-mono text-xs font-medium">
            {field.label}
            {field.required ? <span className="ml-1 text-basalt">(required)</span> : null}
          </span>
        );
        return (
          <div className="flex flex-col gap-2" key={`${field.label}-${index}`}>
            {field.fieldType === "checkbox" ? (
              <label className="flex min-h-11 cursor-pointer items-start gap-3">
                <input
                  {...shared}
                  className="size-5 shrink-0 border-weathered-steel accent-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  type="checkbox"
                />
                {label}
              </label>
            ) : (
              <>
                {<label htmlFor={inputId}>{label}</label>}
                {field.fieldType === "long-text" ? (
                  <textarea
                    {...shared}
                    className={`${controlClass} min-h-36 resize-y py-3`}
                    placeholder={field.placeholder || undefined}
                  />
                ) : field.fieldType === "select" ? (
                  <select {...shared} className={controlClass} defaultValue="">
                    <option disabled value="">
                      {field.placeholder}
                    </option>
                    {field.selectOptions?.map((option, optionIndex) => (
                      <option key={`${option.label}-${optionIndex}`} value={option.label}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    {...shared}
                    className={controlClass}
                    pattern={field.fieldType === "phone" ? "[+()0-9 .-]{7,}" : undefined}
                    placeholder={field.placeholder || undefined}
                    type={
                      field.fieldType === "email"
                        ? "email"
                        : field.fieldType === "phone"
                          ? "tel"
                          : "text"
                    }
                  />
                )}
              </>
            )}
            {error ? (
              <p className="font-sprout-mono text-xs text-oxidized-orange" id={errorId}>
                {error}
              </p>
            ) : null}
          </div>
        );
      })}
      <button
        className="inline-flex min-h-12 w-full items-center justify-center rounded-[14px] border-2 border-primary-700 bg-primary px-6 font-sprout-mono text-sm font-extrabold text-primary-foreground shadow-[0_3px_0_#14213d,0_12px_18px_-14px_rgba(20,33,61,0.55)] transition-[background-color,transform,box-shadow] hover:bg-primary-700 active:translate-y-[2px] active:shadow-[0_1px_0_#14213d] focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring disabled:cursor-wait disabled:opacity-65 sm:w-fit sm:min-w-44"
        disabled={submitting}
        type="submit"
      >
        {submitting ? `${submitLabel}…` : submitLabel}
      </button>
    </form>
  );
};

FormPanel.displayName = "Content::FormPanel";
